'use client;';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';

import { CustomerServiceType, useCustomerCtx } from '@/app/(app)/customers/[id]/provider';
import { useEmployeesCtx } from '@/components/providers/employee-provider';
import { useServiceTypeTemplatesCtx } from '@/components/providers/service-templates-provider';
import { ApiSchemas } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { DayOfWeek } from '@/consts/date-time';

type ServiceEvent = ApiSchemas['CustomerServiceEventRead'];

interface SelectedAvailability {
  routeId: string;
  employeeId: string | null;
  windowStartTime: string;
  windowEndTime: string;
  slot: ApiSchemas['Slot'];
  slots: ApiSchemas['Slot'][];
}

interface TimeWindow {
  start_time: string;
  end_time: string;
}

interface SearchFilters {
  window: TimeWindow | null;
  employeeIds: string[];
  regionIds: string[];
  skillsIds: string[];
  preferredDays: DayOfWeek[];
  blockedEmployeeIds: string[];
}

interface SearchParams extends SearchFilters {
  selectedDate: Date;
  serviceName: string;
  serviceTypeId: string | null;
  serviceTypeTemplateId: string | null;
  servicePlanId: string | null;
  durationMinutes: number;
}

type BookingMode = 'NEW' | 'RESCHEDULE';

interface ContextType {
  mode: BookingMode;
  note: string;
  setNote: (note: string) => void;
  selected: SelectedAvailability | null;
  setSelected: (selected: SelectedAvailability | null) => void;
  params: SearchParams;
  setParams: (params: Partial<SearchParams>) => void;
  isAnyFilterSet: boolean;
  clearFilters: () => void;
  editingServiceEvent: ServiceEvent | null;
  startReschedule: (serviceEvent: ServiceEvent | null) => void;
  cancelReschedule: () => void;
  clearBooking: () => void;
}

const CustomerBookingContext = createContext<ContextType | null>(null);

export function useCustomerBookingCtx() {
  const ctx = useContext(CustomerBookingContext);
  if (!ctx) {
    throw new Error('useCustomerBookingCtx must be used within a CustomerBookingProvider');
  }
  return ctx;
}

interface Props {
  children: React.ReactNode;
}

export function CustomerBookingProvider({ children }: Props) {
  const { serviceTypesById } = useCustomerCtx();
  const { params, setParams, setParamsPartial } = useCustomerSearchParams();
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState<SelectedAvailability | null>(null);
  const [editingServiceEvent, setEditingServiceEvent] = useState<ServiceEvent | null>(null);
  const [mode, setMode] = useState<BookingMode>('NEW');

  const isAnyFilterSet = useMemo(() => {
    const searchFilters: SearchFilters = {
      window: params.window,
      employeeIds: params.employeeIds,
      regionIds: params.regionIds,
      skillsIds: params.skillsIds,
      preferredDays: params.preferredDays,
      blockedEmployeeIds: params.blockedEmployeeIds,
    };
    const emptyFilters = getEmptySearchFilters();
    return isEqual(searchFilters, emptyFilters);
  }, [params]);

  const clearFilters = useCallback(() => {
    setParams((prev) => ({ ...prev, ...getEmptySearchFilters() }));
  }, [setParams]);

  const clearBooking = useCallback(() => {
    clearFilters();
    setEditingServiceEvent(null);
    setSelected(null);
    setParams((prev) => ({ ...prev, serviceTypeId: null, serviceTypeTemplateId: null }));
    setMode('NEW');
  }, [clearFilters, setParams]);

  const startReschedule = useCallback(
    (serviceEvent: ServiceEvent | null) => {
      const serviceType = serviceTypesById.get(serviceEvent?.service_type_id ?? '');
      const newParams: Partial<SearchParams> = {
        serviceName: serviceType?.name ?? '',
        serviceTypeId: serviceEvent?.service_type_id,
        serviceTypeTemplateId: null,
        servicePlanId: serviceType?.service_plan_id ?? null,
      };
      if (serviceEvent?.event.duration_minutes) {
        newParams.durationMinutes = serviceEvent.event.duration_minutes;
      }

      setParamsPartial(newParams);
      setEditingServiceEvent(serviceEvent);
      setNote(serviceEvent?.note?.content ?? '');
      setMode('RESCHEDULE');
    },
    [setEditingServiceEvent, setParamsPartial, setNote, serviceTypesById]
  );

  const cancelReschedule = useCallback(() => {
    setEditingServiceEvent(null);
    setParamsPartial({ serviceTypeId: null, serviceTypeTemplateId: null, servicePlanId: null });
    setMode('NEW');
  }, [setEditingServiceEvent, setParamsPartial]);

  return (
    <CustomerBookingContext.Provider
      value={{
        note,
        setNote,
        params,
        setParams: setParamsPartial,
        selected,
        setSelected,
        isAnyFilterSet,
        clearFilters,
        editingServiceEvent,
        startReschedule,
        cancelReschedule,
        clearBooking,
        mode,
      }}
    >
      {children}
    </CustomerBookingContext.Provider>
  );
}

function getEmptySearchFilters(): SearchFilters {
  return {
    window: null,
    employeeIds: [],
    regionIds: [],
    skillsIds: [],
    preferredDays: [],
    blockedEmployeeIds: [],
  };
}

function useCustomerSearchParams() {
  const { customer, serviceTypesById } = useCustomerCtx();
  const { office } = useOfficeCtx();
  const { employeesById } = useEmployeesCtx();
  const { serviceTypeTemplatesById } = useServiceTypeTemplatesCtx();

  const requiredEmployeeIds = useMemo(() => {
    return customer.preferences.technicians
      .filter((t) => t.preference === 'REQUIRED' && employeesById.get(t.employee_id)?.bookable)
      .map((t) => t.employee_id);
  }, [customer.preferences.technicians, employeesById]);

  const blockedEmployeeIds = useMemo(() => {
    return customer.preferences.technicians
      .filter((t) => t.preference === 'BLOCKED' && employeesById.get(t.employee_id)?.bookable)
      .map((t) => t.employee_id);
  }, [customer.preferences.technicians, employeesById]);

  const [params, setParams] = useState<SearchParams>(() => {
    // Setting the initial search params

    // Select the first (non-cancelled) service plan
    const servicePlan = customer.service_plans.find((servicePlan) => !servicePlan.cancelled_at);

    return {
      servicePlanId: servicePlan?.id ?? null,
      serviceName: '',
      serviceTypeId: null,
      serviceTypeTemplateId: null,
      durationMinutes: 30,
      employeeIds: [],
      window: null,
      regionIds: getRegionIdsByZipCode(office.regions, customer.primary_location?.zip),
      skillsIds: [],
      timeOfDay: 'ANYTIME',
      preferredDays: [],
      blockedEmployeeIds: [],
      selectedDate: new Date(),
    };
  });

  useEffect(() => {
    setParams((prev) => ({
      ...prev,
      employeeIds: requiredEmployeeIds,
      blockedEmployeeIds,
    }));
  }, [requiredEmployeeIds, blockedEmployeeIds]);

  const setParamsPartial = useCallback(
    (newParams: Partial<SearchParams>) => {
      if (newParams.serviceTypeId) {
        const serviceType = serviceTypesById.get(newParams.serviceTypeId);
        const { skillIds, durationMinutes } = getServiceTypeSearchParams(serviceType);
        newParams.skillsIds = skillIds;
        if (!newParams.durationMinutes) newParams.durationMinutes = durationMinutes;
      }
      if (newParams.serviceTypeTemplateId) {
        const template = serviceTypeTemplatesById.get(newParams.serviceTypeTemplateId);
        if (template?.skills) {
          newParams.skillsIds = template.skills.map((s) => s.skill_id);
        }
        if (template?.event_duration_minutes) {
          newParams.durationMinutes = template.event_duration_minutes;
        }
      }
      setParams((prev) => ({ ...prev, ...newParams }));
    },
    [serviceTypesById, serviceTypeTemplatesById]
  );

  return { params, setParams, setParamsPartial };
}

function getRegionIdsByZipCode(regions: ApiSchemas['OfficeRegionRead'][], zip?: string) {
  if (!zip) return [];
  return regions.filter((r) => r.zip_codes?.includes(zip)).map((r) => r.id);
}

function getServiceTypeSearchParams(serviceType: CustomerServiceType | undefined) {
  if (!serviceType) return { skillIds: [], durationMinutes: 30 };

  // Get skills from service type or its template
  const skillIds = serviceType.skills.map((s) => s.skill_id);
  if (skillIds.length === 0 && serviceType.template?.skills) {
    skillIds.push(...serviceType.template.skills.map((s) => s.skill_id));
  }

  const durationMinutes = serviceType.event_duration_minutes ?? serviceType.template?.event_duration_minutes ?? 30;

  return {
    skillIds: skillIds,
    durationMinutes: durationMinutes,
  };
}
